import schemas from 'components/forms/fields/definedSchemas';
import Joi from 'joi';

const widgetFields = {
    //// Select ////
    vessel_id: {
        ...schemas.integerOptional,
        section: 'Select',
        hidden: true,
    },
    new_vessel: {
        ...schemas.textOptional,
        section: 'Select',
        hidden: true,
        validation: Joi.when('vessel_id', {
            is: '',
            then: Joi.string().required().messages({
                'string.empty':
                    'Please either select a boat from My Vessels or Vessel Search, or choose "I don\'t see my boat".',
            }),
            otherwise: Joi.string().allow(null, ''),
        }),
    },
    external_vessel_id: {},
    existing_vessel_id: {},
    origin: {},
};

const fields = {
    //// Name and Numbers ////
    vessel_name: {
        ...schemas.textRequired,
        section: 'Name and Numbers',
        label: 'Vessel Name',
    },
    flag: {
        ...schemas.country,
        section: 'Name and Numbers',
        label: 'What flag is the vessel registered under?',
    },
    // operating_company: {
    //     ...schemas.textRequired,
    //     section: 'Vessel Info',
    //     label: 'Name of Operating Company',
    // },
    uniformed_service_vessel: {
        ...schemas.boolRequired,
        section: 'Name and Numbers',
        label: 'Is this vessel owned and operated by NOAA, the Coast Guard, the Navy, the Army, the Air Force, the Marine Corps, or the Public Health Service?',
        shortLabel: 'Uniformed Service Vessel',
    },
    imo_number: {
        ...schemas.imoOptional,
        section: 'Name and Numbers',
        label: 'IMO Number',
    },
    mmsi: {
        ...schemas.mmsiOptional,
        section: 'Name and Numbers',
        label: 'MMSI',
    },
    uscg_official_number: {
        ...schemas.uscgOfficialNumberOptional,
        section: 'Name and Numbers',
        label: 'USCG Official Number',
    },
    state_registration: {
        ...schemas.stateRegistrationNumberOptional,
        section: 'Name and Numbers',
        label: 'State Registration Number',
    },
    other_registration_number: {
        ...schemas.textOptional,
        section: 'Name and Numbers',
        label: 'Other Registration Number',
        validation: Joi.when(Joi.ref('mmsi'), {
            is: '',
            then: Joi.when(Joi.ref('uscg_official_number'), {
                is: '',
                then: Joi.when(Joi.ref('state_registration'), {
                    is: '',
                    then: Joi.when(Joi.ref('imo_number'), {
                        is: null,
                        then: Joi.string().min(5).max(20).messages({
                            'string.empty':
                                'At least one registration number is required.',
                        }),
                        otherwise: Joi.when(Joi.ref('imo_number'), {
                            is: '',
                            then: Joi.string().min(5).max(20).messages({
                                'string.empty':
                                    'At least one registration number is required.',
                            }),
                            otherwise: Joi.string()
                                .min(5)
                                .max(20)
                                .allow('', null),
                        }),
                    }),
                    otherwise: Joi.string().min(5).max(20).allow('', null),
                }),
                otherwise: Joi.string().min(5).max(20).allow('', null),
            }),
            otherwise: Joi.string().min(5).max(20).allow('', null),
        }),
    },
    //// Tonnage and Type ////
    tonnage: {
        ...schemas.floatRequired,
        section: 'Tonnage and Type',
        label: 'Tonnage',
    },
    tonnage_type: {
        ...schemas.tonnageTypeRequired,
        section: 'Tonnage and Type',
        label: 'Tonnage Type',
    },
    length_tonnage: {
        ...schemas.floatRequired,
        section: 'Tonnage and Type',
        label: 'Vessel Length (feet)',
    },
    width_tonnage: {
        ...schemas.floatOptional,
        section: 'Tonnage and Type',
        label: 'Vessel Width (feet)',
    },
    depth_tonnage: {
        ...schemas.floatOptional,
        section: 'Tonnage and Type',
        label: 'Vessel Depth (feet)',
    },
    vessel_type: {
        ...schemas.radioRequired,
        section: 'Tonnage and Type',
        label: 'Vessel Type',
        options: {
            'Motor Vessel': 'Motor Vessel',
            'Sailing Vessel': 'Sailing Vessel',
            'Towing Vessel': 'Towing Vessel',
            'Tank Ship': 'Tank Ship',
            'Tank Barge': 'Tank Barge',
            'Offshore Installation': 'Offshore Installation',
            'High Speed Craft': 'High Speed Craft',
            Liftboat: 'Liftboat',
        },
        initialValue: 'Motor Vessel',
    },
    tankship_or_barge_type: {
        ...schemas.radioRequired,
        section: 'Tonnage and Type',
        label: 'Tankship or Barge type',
        name: 'tankship_or_barge_type',
        options: {
            'Liquefied Gas': 'Liquefied Gas',
            'Dangerous Liquid': 'Dangerous Liquid',
            Chemical: 'Chemical',
        },
        validation: Joi.when('vessel_type', {
            is: 'Tank Ship',
            then: Joi.string().required(),
            otherwise: Joi.when('vessel_type', {
                is: 'Tank Barge',
                then: Joi.string().required(),
                otherwise: Joi.string().allow(''),
            }),
        }),
    },
    engine_horsepower: {
        ...schemas.floatRequired,
        section: 'Tonnage and Type',
        label: 'Vessel Horsepower',
    },
    engine_mode_of_propulsion: {
        ...schemas.selectRequired,
        section: 'Tonnage and Type',
        label: 'Propulsion Type',
        options: {
            Motor: 'Motor',
            Steam: 'Steam',
            'Gas Turbine': 'Gas Turbine',
        },
        initialValue: 'Motor',
    },
    //// Offshore ////
    offshore_installation_type: {
        ...schemas.radioRequired,
        section: 'Offshore Installation',
        label: 'What type of Offshore Installation is this vessel?',
        options: {
            'Mobile Offshore Drilling Unit': 'Mobile Offshore Drilling Unit',
            'Floating Offshore Installation': 'Floating Offshore Installation',
            'Fixed Platform': 'Fixed Platform',
        },
        validation: Joi.when('vessel_type', {
            is: 'Offshore Installation',
            then: Joi.string().required(),
            otherwise: Joi.string().allow(''),
        }),
    },
    offshore_installation_MODU_type: {
        ...schemas.radioRequired,
        section: 'Offshore Installation',
        label: 'What type of Mobile Offshore Drilling Unit is this vessel?',
        options: {
            'Bottom Bearing Unit': 'Bottom Bearing Unit',
            'Surface Unit': 'Surface Unit',
        },
        validation: Joi.when('offshore_installation_type', {
            is: 'Mobile Offshore Drilling Unit',
            then: Joi.string().required(),
            otherwise: Joi.string().allow(''),
        }),
    },
    offshore_installation_MODU_propulsion_type: {
        ...schemas.radioRequired,
        section: 'Vessel Information',
        label: 'Is this vessel a self-propelled or propulsion assisted Mobile Offshore Drilling Unit?',
        options: {
            'Self Propelled': 'Self Propelled',
            'Propulsion Assisted': 'Propulsion Assisted',
            'Non-Self-Propelled': 'Non-Self-Propelled',
        },
        validation: Joi.when('offshore_installation_type', {
            is: 'Mobile Offshore Drilling Unit',
            then: Joi.string().required(),
            otherwise: Joi.string().allow(''),
        }),
    },
    offshore_installation_FOI_type: {
        ...schemas.radioRequired,
        section: 'Offshore Installation',
        label: 'What type of Ballast does the Floating Offshore Installation vessel use?',
        options: {
            'Active Ballast': 'Active Ballast',
            'Passive Ballast': 'Passive Ballast',
        },
        validation: Joi.when('offshore_installation_type', {
            is: 'Floating Offshore Installation',
            then: Joi.string().required(),
            otherwise: Joi.string().allow(''),
        }),
    },
};

const shortFields = {
    ...fields,
    uniformed_service_vessel: {
        ...fields.uniformed_service_vessel,
        label: 'Uniformed Service Vessel',
    },
    vessel_type: {
        ...fields.vessel_type,
        component: schemas.selectRequired.component,
    },
    tankship_or_barge_type: {
        ...fields.tankship_or_barge_type,
        component: schemas.selectRequired.component,
        label: 'Tank Ship or Barge Type',
    },
    offshore_installation_type: {
        ...fields.offshore_installation_type,
        component: schemas.selectRequired.component,
        label: 'Offshore Installation Type',
    },
    offshore_installation_MODU_type: {
        ...fields.offshore_installation_MODU_type,
        component: schemas.selectRequired.component,
        label: 'MODU Type',
    },
    offshore_installation_MODU_propulsion_type: {
        ...fields.offshore_installation_MODU_propulsion_type,
        component: schemas.selectRequired.component,
        label: 'MODU Propulsion',
    },
    offshore_installation_FOI_type: {
        ...fields.offshore_installation_FOI_type,
        component: schemas.selectRequired.component,
        label: 'FOI Ballast',
    },
};

const allVesselFields = {...fields, ...widgetFields};

export default fields;
export {widgetFields, shortFields, allVesselFields};
